import type { PlayerId } from '@/types/player.types';
import type { BoardPosition } from '@/types/warrior.types';

/**
 * Network role (host creates room, guest joins)
 */
export type NetworkRole = 'host' | 'guest';

/**
 * Connection status
 */
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * Network messages exchanged between peers
 */
export type NetworkMessage =
  // Team selection phase
  | { type: 'TEAM_READY'; playerName: string; warriorIds: string[]; rating: number }
  // Coin flip result (sent by host)
  | { type: 'COIN_FLIP_RESULT'; startingPlayer: PlayerId }
  // Battle phase - warrior selection
  | { type: 'SELECT_WARRIOR'; warriorInstanceId: string }
  // Battle phase - actions
  | { type: 'EXECUTE_MOVE'; to: BoardPosition }
  | { type: 'EXECUTE_DASH'; to: BoardPosition }
  | { type: 'EXECUTE_ATTACK' }
  | { type: 'EXECUTE_HEAL' }
  // Game end
  | { type: 'REMATCH_REQUEST' }
  | { type: 'DISCONNECT' };

/**
 * Network state for tracking connection
 */
export interface NetworkState {
  role: NetworkRole | null;
  roomId: string | null;
  connectionStatus: ConnectionStatus;
  remotePlayerReady: boolean;
}

/**
 * Event handlers for network events
 */
export interface NetworkEventHandlers {
  onConnected?: () => void;
  onDisconnected?: () => void;
  onError?: (error: Error) => void;
  onMessage?: (message: NetworkMessage) => void;
}

/**
 * Get the local player ID based on network role
 */
export function getLocalPlayerId(role: NetworkRole): PlayerId {
  return role === 'host' ? 'player1' : 'player2';
}

/**
 * Get the remote player ID based on network role
 */
export function getRemotePlayerId(role: NetworkRole): PlayerId {
  return role === 'host' ? 'player2' : 'player1';
}
